import axios from 'axios'
import { axiosError } from './clientUtils'
import { useAuthStore } from '@/stores/authStore'
import { getLytAccountingApiBase } from './baseUrls'

// ─── Shared Link types (mirrored from useEntityLinkStudio for API client use ───
export type PrerequisiteDescriptor = {
  type: 'LINE_ITEM' | 'DEBTOR'
  lytsoftId: string
  deepLink: string
}

export type LinkResponse<T> = {
  row: T | null
  alreadyAssociated: boolean
  prerequisites?: PrerequisiteDescriptor[]
  error?: string
}

/** Map 409/422 axios errors into LinkResponse while preserving backend body fields. */
function classifyLinkError<T>(err: unknown): LinkResponse<T> {
  const axiosErr = err as {
    response?: {
      status?: number
      data?: {
        row?: T | null
        alreadyAssociated?: boolean
        error?: string
        prerequisites?: PrerequisiteDescriptor[]
      }
    }
  }
  const status = axiosErr.response?.status
  const data = axiosErr.response?.data
  if (status === 409) {
    return {
      row: data?.row ?? null,
      alreadyAssociated: data?.alreadyAssociated ?? false,
      error: 'CONFLICT',
    }
  }
  if (status === 422) {
    return {
      row: null,
      alreadyAssociated: false,
      prerequisites: data?.prerequisites ?? [],
      error: data?.error ?? 'UNRESOLVED_PREREQUISITES',
    }
  }
  throw err
}

const BASE_URL = getLytAccountingApiBase()

function sleep(ms: number) { return new Promise(r => setTimeout(r, ms)) }

function authHeaders(tokenOrApiKey?: string) {
  if (tokenOrApiKey) return { Authorization: `Bearer ${tokenOrApiKey}` }
  const apiKey = useAuthStore().currentApiKey
  return apiKey ? { 'X-API-Key': apiKey } : {}
}

async function getWithRetry<T>(path: string, config = {}, retries = 2): Promise<T> {
  let attempt = 0
  let delay = 300
  while (true) {
    try {
      const url = `${BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`
      const res = await axios.get<T>(url, { ...config })
      return res.data
    } catch (err) {
      if (!shouldRetryGet(err, attempt, retries)) throw axiosError(err)
      await sleep(delay)
      delay *= 2
      attempt++
    }
  }
}

function shouldRetryGet(err: unknown, attempt: number, retries: number) {
  if (attempt >= retries) return false
  const maybeAxios = err as { code?: string; response?: { status?: number } }
  if (maybeAxios.code === 'ERR_CANCELED' || (err as Error)?.name === 'AbortError') return false
  const status = maybeAxios.response?.status
  if (!status) return true
  if ([401, 403, 404, 409, 422, 429].includes(status)) return false
  return status >= 500
}
function shouldRetryPost(err: unknown, attempt: number, retries: number) {
  if (attempt >= retries) return false
  const maybeAxios = err as { code?: string; response?: { status?: number } }
  const status = maybeAxios.response?.status
  if (!status) return true  // Network error, retry
  if ([401, 403, 404, 409, 422, 429].includes(status)) return false  // Client error, don't retry
  return status >= 500  // Server error, retry
}

export interface Package {
  id: string
  name: string
  status: string
  description: string
  loginType: string | null
  imageUrl: string | null
}

interface PackageResponse {
  packages?: Package[]
}

export async function getPackages(params: { invoiceNumber: string; storeId: string; token?: string }): Promise<Package[]> {
  const { invoiceNumber, storeId, token } = params
  if (!invoiceNumber || !storeId) throw new Error('invoiceNumber and storeId are required')
  const qs = new URLSearchParams({ invoiceNumber, storeId }).toString()
  const resp = await getWithRetry<PackageResponse | Package[]>(`/api/v1/setup/packages?${qs}`, {
    headers: { ...authHeaders(token) },
  })
  const list = Array.isArray(resp) ? resp : (Array.isArray(resp?.packages) ? resp.packages : [])
  return list.map(p => ({
    id: p.id,
    name: p.name || (p as unknown as { displayName?: string }).displayName || p.id,
    status: p.status || 'ACTIVE',
    description: p.description || '',
    loginType: p.loginType || null,
    imageUrl: p.imageUrl ?? null,
  }))
}

export async function verifyAccountDetails(
  packageId: string,
  payload: { invoiceNumber: string; storeId: string; credentials?: Record<string, unknown> },
  token?: string,
) {
  if (!packageId) throw new Error('packageId is required')
  const { invoiceNumber, storeId, credentials } = payload || {}
  if (!invoiceNumber || !storeId) throw new Error('invoiceNumber and storeId are required')
  const qs = new URLSearchParams({ invoiceNumber, storeId }).toString()
  const url = `${BASE_URL}/api/v1/setup/providers/${encodeURIComponent(packageId)}/verify?${qs}`
  try {
    const res = await axios.post(url, credentials || {}, {
      headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    })
    return res.data
  } catch (err) {
    throw axiosError(err)
  }
}

export async function getDashboardStatus(apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry('/api/v1/dashboard/status', { headers })
}

export async function getCustomerStatus(lytsoftCustomerId: string, apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry(`/api/v1/customers/${encodeURIComponent(lytsoftCustomerId)}/status`, { headers })
}

export async function syncAllCustomers(customers: unknown[], apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/customers/batch`
  const res = await axios.post(url, customers, { headers: { 'Content-Type': 'application/json', ...headers } })
  return res.data
}

export async function getItemStatus(lytsoftItemId: string, apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry(`/api/v1/items/${encodeURIComponent(lytsoftItemId)}/status`, { headers })
}

export async function syncAllItems(items: unknown[], apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/items/batch`
  const res = await axios.post(url, items, { headers: { 'Content-Type': 'application/json', ...headers } })
  return res.data
}

export async function getInvoiceStatus(lytsoftInvoiceId: string, apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry(`/api/v1/invoices/${encodeURIComponent(lytsoftInvoiceId)}/status`, { headers })
}

export async function getPaymentStatus(lytsoftPaymentId: string, apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry(`/api/v1/payments/${encodeURIComponent(lytsoftPaymentId)}/status`, { headers })
}

// --- Type definitions for stock async API ---

export interface StockAsyncRequestDTO {
  dedupeKey?: string;
  scope?: string;
  batchSize?: number;
  force?: boolean;
}

export interface StockAsyncResponseDTO {
  taskId: number;
  parentTaskId: number | null;
  status: string;
  acceptedAt: string;
  entityType: string;
  operation: string;
  totalChildren: number | null;
  completedChildren: number;
  failedChildren: number;
}

export interface StockTaskDetailDTO {
  taskId: number;
  status: string;
  acceptedAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt?: string | null;
  entityType: string;
  entityId?: string | null;
  externalId?: string | null;
  operation: string;
  scope: string;
  parentTaskId?: number | null;
  attemptCount?: number | null;
  maxAttempts?: number | null;
  totalChildren: number;
  queuedChildren: number;
  runningChildren: number;
  completedChildren: number;
  failedChildren: number;
  cancelledChildren: number;
  skippedChildren: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

// --- Stock async API functions ---

/**
 * POST /api/v1/stock/async
 * Triggers an async stock full-sync. Returns 202 with taskId.
 */
export async function postStockAsync(request: StockAsyncRequestDTO = {}, apiKey?: string): Promise<StockAsyncResponseDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/stock/async`
  try {
    const res = await axios.post<StockAsyncResponseDTO>(url, request, {
      headers: { 'Content-Type': 'application/json', ...headers }
    })
    return res.data
  } catch (err) {
    throw axiosError(err)
  }
}

/**
 * GET /api/v1/stock/tasks/{taskId}
 * Fetches stock-specific task detail with child counter fields.
 */
export async function getStockTaskDetail(taskId: number | string, apiKey?: string): Promise<StockTaskDetailDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry<StockTaskDetailDTO>(`/api/v1/stock/tasks/${taskId}`, { headers })
}

/**
 * GET /api/v1/stock/tasks/active
 * Fetches all active (non-terminal) stock sync tasks.
 */
export async function getActiveStockTasks(apiKey?: string): Promise<StockTaskDetailDTO[]> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry<StockTaskDetailDTO[]>(`/api/v1/stock/tasks/active`, { headers })
}

// --- Item async types ---

export interface ItemAsyncRequestDTO {
  dedupeKey?: string;
  scope?: string;
  force?: boolean;
  entityId?: string;
}

export interface ItemAsyncResponseDTO {
  taskId: number;
  parentTaskId: number | null;
  status: string;
  acceptedAt: string;
  entityType: string;
  operation: string;
}

export interface ItemTaskDetailDTO {
  taskId: number;
  status: string;
  acceptedAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt?: string | null;
  entityType: string;
  operation: string;
  scope: string;
  entityId: string;
  externalId?: string | null;
  parentTaskId?: number | null;
  attemptCount?: number | null;
  maxAttempts?: number | null;
  totalChildren: number;
  queuedChildren?: number | null;
  runningChildren?: number | null;
  completedChildren: number;
  failedChildren: number;
  cancelledChildren: number;
  skippedChildren: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

// --- Customer async types ---

export interface CustomerAsyncRequestDTO {
  dedupeKey?: string;
  scope?: string;
  force?: boolean;
  entityId?: string;
}

export interface CustomerAsyncResponseDTO {
  taskId: number;
  parentTaskId: number | null;
  status: string;
  acceptedAt: string;
  entityType: string;
  operation: string;
}

export interface CustomerTaskDetailDTO {
  taskId: number;
  status: string;
  acceptedAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt?: string | null;
  entityType: string;
  operation: string;
  scope: string;
  entityId: string;
  externalId?: string | null;
  parentTaskId?: number | null;
  attemptCount?: number | null;
  maxAttempts?: number | null;
  totalChildren: number;
  queuedChildren?: number | null;
  runningChildren?: number | null;
  completedChildren: number;
  failedChildren: number;
  cancelledChildren: number;
  skippedChildren: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

// --- Invoice async types ---

export interface InvoiceAsyncRequestDTO {
  dedupeKey?: string;
  scope?: string;
  force?: boolean;
  entityId?: string;
  invoiceSnapshot?: unknown;
}

export interface InvoiceAsyncResponseDTO {
  taskId: number;
  parentTaskId: number | null;
  status: string;
  acceptedAt: string;
  entityType: string;
  operation: string;
}

export interface InvoiceTaskDetailDTO {
  taskId: number;
  status: string;
  acceptedAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt?: string | null;
  entityType: string;
  operation: string;
  scope: string;
  entityId: string;
  externalId?: string | null;
  parentTaskId?: number | null;
  attemptCount?: number | null;
  maxAttempts?: number | null;
  totalChildren: number;
  queuedChildren?: number | null;
  runningChildren?: number | null;
  completedChildren: number;
  failedChildren: number;
  cancelledChildren: number;
  skippedChildren: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

// --- Payment async types ---

export interface PaymentAsyncRequestDTO {
  dedupeKey?: string;
  scope?: string;
  force?: boolean;
  entityId?: string;
  paymentSnapshot?: unknown;
}

export interface PaymentAsyncResponseDTO {
  taskId: number;
  parentTaskId: number | null;
  status: string;
  acceptedAt: string;
  entityType: string;
  operation: string;
}

export interface PaymentTaskDetailDTO {
  taskId: number;
  status: string;
  acceptedAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt?: string | null;
  entityType: string;
  operation: string;
  scope: string;
  entityId: string;
  externalId?: string | null;
  parentTaskId?: number | null;
  attemptCount?: number | null;
  maxAttempts?: number | null;
  totalChildren: number;
  queuedChildren?: number | null;
  runningChildren?: number | null;
  completedChildren: number;
  failedChildren: number;
  cancelledChildren: number;
  skippedChildren: number;
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

/**
 * POST /api/v1/items/async
 * Triggers an async item sync. Returns 202 with taskId.
 */

export async function postItemAsync(request: ItemAsyncRequestDTO = {}, apiKey?: string): Promise<ItemAsyncResponseDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/items/async`
  try {
    const res = await axios.post<ItemAsyncResponseDTO>(url, request, {
      headers: { 'Content-Type': 'application/json', ...headers }
    })
    return res.data
  } catch (err) {
    throw axiosError(err)
  }
}

/**
 * GET /api/v1/items/tasks/{taskId}
 * Fetches item-specific task detail.
 */
export async function getItemTaskDetail(taskId: number | string, apiKey?: string): Promise<ItemTaskDetailDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry<ItemTaskDetailDTO>(`/api/v1/items/tasks/${taskId}`, { headers })
}

/**
 * GET /api/v1/items/tasks/active
 * Fetches all active (non-terminal) item sync tasks.
 */
export async function getActiveItemTasks(apiKey?: string): Promise<ItemTaskDetailDTO[]> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry<ItemTaskDetailDTO[]>(`/api/v1/items/tasks/active`, { headers })
}

/**
 * POST /api/v1/customers/async
 * Triggers an async customer sync. Returns 202 with taskId.
 */
export async function postCustomerAsync(request: CustomerAsyncRequestDTO = {}, apiKey?: string): Promise<CustomerAsyncResponseDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/customers/async`
  try {
    const res = await axios.post<CustomerAsyncResponseDTO>(url, request, {
      headers: { 'Content-Type': 'application/json', ...headers }
    })
    return res.data
  } catch (err) {
    throw axiosError(err)
  }
}

/**
 * POST /api/v1/invoices/async
 * Triggers an async invoice sync. Returns 202 with taskId.
 */
export async function postInvoiceAsync(request: InvoiceAsyncRequestDTO = {}, apiKey?: string): Promise<InvoiceAsyncResponseDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/invoices/async`
  try {
    const res = await axios.post<InvoiceAsyncResponseDTO>(url, request, {
      headers: { 'Content-Type': 'application/json', ...headers }
    })
    return res.data
  } catch (err) {
    throw axiosError(err)
  }
}

/**
 * POST /api/v1/payments/async
 * Triggers an async payment sync. Returns 202 with taskId.
 */
export async function postPaymentAsync(request: PaymentAsyncRequestDTO = {}, apiKey?: string): Promise<PaymentAsyncResponseDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/payments/async`
  try {
    const res = await axios.post<PaymentAsyncResponseDTO>(url, request, {
      headers: { 'Content-Type': 'application/json', ...headers }
    })
    return res.data
  } catch (err) {
    throw axiosError(err)
  }
}
/**
 * GET /api/v1/customers/tasks/{taskId}
 * Fetches customer-specific task detail.
 */

export async function getCustomerTaskDetail(taskId: number | string, apiKey?: string): Promise<CustomerTaskDetailDTO> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry<CustomerTaskDetailDTO>(`/api/v1/customers/tasks/${taskId}`, { headers })
}

/**
 * GET /api/v1/customers/tasks/active
 * Fetches all active (non-terminal) customer sync tasks.
 */
export async function getActiveCustomerTasks(apiKey?: string): Promise<CustomerTaskDetailDTO[]> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  return getWithRetry<CustomerTaskDetailDTO[]>(`/api/v1/customers/tasks/active`, { headers })
}

export async function syncAllStock(apiKey?: string) {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/stock/sync`
  const res = await axios.post(url, {}, { headers: { 'Content-Type': 'application/json', ...headers } })
  return res.data
}

// --- Task visibility API (Phase 5 Wave 2) ---

export interface TaskSummaryDto {
  taskId: number
  entityType: string
  status: string
  externalId: string | null
  attemptCount: number
  createdAt: string
  updatedAt: string
  completedAt: string | null
  errorMessage: string | null
  parentTaskId?: number | null
  totalChildren?: number | null
  completedChildren?: number | null
}

export interface TaskDetail {
  taskId: number
  entityType: string
  entityId: string | null
  externalId: string | null
  operation: string | null
  scope: string | null
  status: string
  dedupeKey: string | null
  parentTaskId: number | null
  createdAt: string | null
  acceptedAt: string | null
  startedAt: string | null
  updatedAt: string | null
  finishedAt: string | null
  attemptCount: number | null
  maxAttempts: number | null
  totalChildren: number | null
  queuedChildren: number | null
  runningChildren: number | null
  completedChildren: number | null
  failedChildren: number | null
  cancelledChildren: number | null
  skippedChildren: number | null
  lastErrorCode: string | null
  lastErrorMessage: string | null
}

export interface TaskActionResponse {
  taskId: number
  status: string
  message: string
}

export interface TaskRecentResponse {
  tasks: TaskSummaryDto[]
  total: number
}

export interface EntityHealthSummary {
  entityType: string
  activeCount: number
  completedCount: number
  failedCount: number
  deadLetteredCount: number
  lastSuccessAt: string | null
  lastFailureAt: string | null
  healthStatus: 'healthy' | 'degraded' | 'error'
}

export interface TaskSummaryResponse {
  entitySummaries: EntityHealthSummary[]
}

export interface TaskActiveResponse {
  tasksByEntityType: Record<string, TaskDetail[]>
}

export interface TaskActivityResponse {
  summary?: TaskSummaryResponse | null
  recent?: TaskRecentResponse | null
  active?: TaskActiveResponse | null
  serverTime: string
}

export interface TaskActivityParams {
  recentLimit?: number
  entityTypes?: string[]
  includeSummary?: boolean
  includeRecent?: boolean
  includeActive?: boolean
}

export async function getRecentTasks(params: {
  limit?: number
  entityType?: string
  status?: string
  beforeTimestamp?: string
} = {}, options: { signal?: AbortSignal } = {}): Promise<TaskRecentResponse> {
  const query = new URLSearchParams()
  if (params.limit) query.set('limit', String(params.limit))
  if (params.entityType) query.set('entityType', params.entityType)
  if (params.status) query.set('status', params.status)
  if (params.beforeTimestamp) query.set('beforeTimestamp', params.beforeTimestamp)
  return getWithRetry(`/api/v1/tasks/recent?${query}`, { headers: authHeaders(), signal: options.signal })
}

export async function getTaskChildren(
  taskId: number | string,
  options: { signal?: AbortSignal } = {},
): Promise<TaskRecentResponse> {
  if (taskId === null || taskId === undefined || taskId === '') throw new Error('taskId is required')
  return getWithRetry<TaskRecentResponse>(
    `/api/v1/tasks/${encodeURIComponent(String(taskId))}/children`,
    { headers: authHeaders(), signal: options.signal },
  )
}

export async function getTaskActivity(params: TaskActivityParams = {}, options: { signal?: AbortSignal } = {}): Promise<TaskActivityResponse> {
  const query = new URLSearchParams()
  if (params.recentLimit) query.set('recentLimit', String(params.recentLimit))
  if (params.entityTypes?.length) query.set('entityTypes', params.entityTypes.join(','))
  if (params.includeSummary !== undefined) query.set('includeSummary', String(params.includeSummary))
  if (params.includeRecent !== undefined) query.set('includeRecent', String(params.includeRecent))
  if (params.includeActive !== undefined) query.set('includeActive', String(params.includeActive))
  const suffix = query.toString()
  return getWithRetry(`/api/v1/tasks/activity${suffix ? `?${suffix}` : ''}`, {
    headers: authHeaders(),
    signal: options.signal,
  }, 0)
}

export async function getTaskDetail(taskId: number | string, options: { signal?: AbortSignal } = {}): Promise<TaskDetail> {
  if (taskId === null || taskId === undefined || taskId === '') throw new Error('taskId is required')
  return getWithRetry<TaskDetail>(`/api/v1/tasks/${encodeURIComponent(String(taskId))}`, {
    headers: authHeaders(),
    signal: options.signal,
  })  // Use default 2 retries instead of explicit 0
}
export async function retryTask(taskId: number | string): Promise<TaskActionResponse> {
  if (taskId === null || taskId === undefined || taskId === '') throw new Error('taskId is required')
  const url = `${BASE_URL}/api/v1/tasks/${encodeURIComponent(String(taskId))}/retry`
  let attempt = 0
  let delay = 300
  while (true) {
    try {
      const res = await axios.post<TaskActionResponse>(url, {}, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      })
      return res.data
    } catch (err) {
      if (!shouldRetryPost(err, attempt, 2)) throw axiosError(err)
      await sleep(delay)
      delay *= 2
      attempt++
    }
  }
}
export async function cancelTask(taskId: number | string): Promise<TaskActionResponse> {
  if (taskId === null || taskId === undefined || taskId === '') throw new Error('taskId is required')
  const url = `${BASE_URL}/api/v1/tasks/${encodeURIComponent(String(taskId))}/cancel`
  let attempt = 0
  let delay = 300
  while (true) {
    try {
      const res = await axios.post<TaskActionResponse>(url, {}, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      })
      return res.data
    } catch (err) {
      if (!shouldRetryPost(err, attempt, 2)) throw axiosError(err)
      await sleep(delay)
      delay *= 2
      attempt++
    }
  }
}

export async function getTaskSummary(): Promise<TaskSummaryResponse> {
  return getWithRetry('/api/v1/tasks/summary', { headers: authHeaders() })
}

// --- Association listing types (Phase 11) ---

export interface ItemAssociationRow {
  lytsoftItemId: string
  itemName: string
  itemType: string | null
  unitPrice: number | null
  currencySymbol: string | null
  qboItemId: string | null   // wire-literal field name per D-10
  syncedAt: string | null
  runningTaskId: number | null
}

export interface CustomerAssociationRow {
  lytsoftCustomerId: string
  customerName: string
  email: string | null
  phone: string | null
  qboCustomerId: string | null   // wire-literal field name per D-10
  syncedAt: string | null
  runningTaskId: number | null
}

export interface ItemAssociationListResponse {
  items: ItemAssociationRow[]
  page: number
  pageSize: number
  total: number
}

export interface CustomerAssociationListResponse {
  customers: CustomerAssociationRow[]
  page: number
  pageSize: number
  total: number
}

export interface InvoiceAssociationRow {
  lytsoftInvoiceId: string
  invoiceDate: string | null
  saleNumber: number | null
  totalAmount: number | null
  paymentType: string | null
  customerId: string | null
  customerName: string | null
  currencySymbol: string | null
  qboInvoiceId: string | null
  syncedAt: string | null
  runningTaskId: number | null
}

export interface InvoiceAssociationListResponse {
  invoices: InvoiceAssociationRow[]
  page: number
  pageSize: number
  total: number
}

export interface PaymentAssociationRow {
  lytsoftPaymentId: string
  paymentDate: string | null
  totalAmount: number | null
  paymentType: string | null
  customerId: string | null
  customerName: string | null
  currencySymbol: string | null
  qboPaymentId: string | null
  syncedAt: string | null
  runningTaskId: number | null
  linkedInvoiceNumbers: string[]
}

export interface PaymentAssociationListResponse {
  payments: PaymentAssociationRow[]
  page: number
  pageSize: number
  total: number
}

export interface BulkUnassociatedResponse {
  taskId: number | null
  totalUnassociated: number
  status: string  // "queued", "no_records"
}

/**
 * GET /api/v1/items/associations
 * Fetches paginated item association list with optional status filter.
 */
export async function getItemAssociations(params: {
  page?: number
  pageSize?: number
  status?: string
} = {}, apiKey?: string): Promise<ItemAssociationListResponse> {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.status) query.set('status', params.status)
  return getWithRetry(`/api/v1/items/associations?${query}`, { headers: authHeaders(apiKey) })
}

/**
 * GET /api/v1/customers/associations
 * Fetches paginated customer association list with optional status filter.
 */
export async function getCustomerAssociations(params: {
  page?: number
  pageSize?: number
  status?: string
} = {}, apiKey?: string): Promise<CustomerAssociationListResponse> {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.status) query.set('status', params.status)
  return getWithRetry(`/api/v1/customers/associations?${query}`, { headers: authHeaders(apiKey) })
}

/**
 * GET /api/v1/invoices/associations
 * Fetches paginated invoice association list with optional status filter.
 */
export async function getInvoiceAssociations(params: {
  page?: number
  pageSize?: number
  status?: string
} = {}, apiKey?: string): Promise<InvoiceAssociationListResponse> {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.status) query.set('status', params.status)
  return getWithRetry(`/api/v1/invoices/associations?${query}`, { headers: authHeaders(apiKey) })
}

/**
 * GET /api/v1/payments/associations
 * Fetches paginated payment association list with optional status filter.
 */
export async function getPaymentAssociations(params: {
  page?: number
  pageSize?: number
  status?: string
} = {}, apiKey?: string): Promise<PaymentAssociationListResponse> {
  const query = new URLSearchParams()
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  if (params.status) query.set('status', params.status)
  return getWithRetry(`/api/v1/payments/associations?${query}`, { headers: authHeaders(apiKey) })
}

/**
 * POST /api/v1/items/unassociated
 * Triggers bulk sync for all unassociated items. Returns 202 with taskId, or 200 with no_records.
 */
export async function postItemsUnassociated(apiKey?: string): Promise<BulkUnassociatedResponse> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/items/unassociated`
  const res = await axios.post<BulkUnassociatedResponse>(url, {}, {
    headers: { 'Content-Type': 'application/json', ...headers }
  })
  return res.data
}

/**
 * POST /api/v1/customers/unassociated
 * Triggers bulk sync for all unassociated customers. Returns 202 with taskId, or 200 with no_records.
 */
export async function postCustomersUnassociated(apiKey?: string): Promise<BulkUnassociatedResponse> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/customers/unassociated`
  const res = await axios.post<BulkUnassociatedResponse>(url, {}, {
    headers: { 'Content-Type': 'application/json', ...headers }
  })
  return res.data
}

/**
 * POST /api/v1/invoices/unassociated
 * Triggers bulk sync for all unassociated invoices. Returns 202 with taskId, or 200 with no_records.
 */
export async function postInvoicesUnassociated(apiKey?: string): Promise<BulkUnassociatedResponse> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/invoices/unassociated`
  const res = await axios.post<BulkUnassociatedResponse>(url, {}, {
    headers: { 'Content-Type': 'application/json', ...headers }
  })
  return res.data
}

/**
 * POST /api/v1/payments/unassociated
 * Triggers bulk sync for all unassociated payments. Returns 202 with taskId, or 200 with no_records.
 */
export async function postPaymentsUnassociated(apiKey?: string): Promise<BulkUnassociatedResponse> {
  const headers = apiKey ? { 'X-API-Key': apiKey } : authHeaders()
  const url = `${BASE_URL}/api/v1/payments/unassociated`
  const res = await axios.post<BulkUnassociatedResponse>(url, {}, {
    headers: { 'Content-Type': 'application/json', ...headers }
  })
  return res.data
}
// Alias for the plan's lytAccountingClient naming convention
export const lytAccountingClient = {
  getRecentTasks,
  getTaskDetail,
  getTaskActivity,
  getTaskChildren,
  retryTask,
  cancelTask,
  getTaskSummary,
}

// --- Item QBO Association functions (Phase 12) ---
/**
 * POST /api/v1/items/associate-qbo
 * Associates a QBO item with a Lytning item (Mode A: link existing, Mode B: create new).
 * Mode A: pass both qboItemId and lytsoftItemId
 * Mode B: pass only qboItemId — creates new Lytning item via franchise API
 */
export async function associateQboItem(
  qboItemId: string,
  lytsoftItemId?: string
): Promise<{ success: boolean; message: string }> {
  const body: Record<string, string> = { qboItemId }
  if (lytsoftItemId) body.lytsoftItemId = lytsoftItemId
  try {
    await axios.post(`${BASE_URL}/api/v1/items/associate-qbo`, body, {
      headers: { ...authHeaders(), 'Content-Type': 'application/json' }
    })
    return { success: true, message: 'Item associated successfully' }
  } catch (err) {
    const axiosErr = err as { response?: { status?: number; data?: { error?: string } } }
    const status = axiosErr.response?.status
    const message = axiosErr.response?.data?.error || 'Association failed'
    if (status === 409) return { success: false, message: `Conflict: ${message}` }
    if (status === 404) return { success: false, message: `Not found: ${message}` }
    if (status === 502) return { success: false, message: `Franchise API error: ${message}` }
    return { success: false, message }
  }
}
/**
 * PUT /api/v1/items/association/{lytsoftItemId}
 * Updates the QBO item association for an existing Lytning item.
 */
export async function updateItemAssociation(
  lytsoftItemId: string,
  qboItemId: string
): Promise<{ success: boolean; message: string }> {
  try {
    await axios.put(
      `${BASE_URL}/api/v1/items/association/${encodeURIComponent(lytsoftItemId)}`,
      { qboItemId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    )
    return { success: true, message: 'Association updated' }
  } catch (err) {
    const axiosErr = err as { response?: { status?: number; data?: { error?: string } } }
    const status = axiosErr.response?.status
    const message = axiosErr.response?.data?.error || 'Update failed'
    if (status === 409) return { success: false, message: `Conflict: ${message}` }
    if (status === 404) return { success: false, message: `Not found: ${message}` }
    return { success: false, message }
  }
}
// --- Customer QBO Association functions (Phase 12) ---
/**
 * POST /api/v1/customers/associate-qbo
 * Associates a QBO customer with a Lytning debtor (Mode A: link existing, Mode B: create new).
 * Mode A: pass both qboCustomerId and lytsoftCustomerId
 * Mode B: pass only qboCustomerId — creates new Lytning customer via franchise API
 */
export async function associateQboCustomer(
  qboCustomerId: string,
  lytsoftCustomerId?: string
): Promise<{ success: boolean; message: string }> {
  const body: Record<string, string> = { qboCustomerId }
  if (lytsoftCustomerId) body.lytsoftCustomerId = lytsoftCustomerId
  try {
    await axios.post(`${BASE_URL}/api/v1/customers/associate-qbo`, body, {
      headers: { ...authHeaders(), 'Content-Type': 'application/json' }
    })
    return { success: true, message: 'Customer associated successfully' }
  } catch (err) {
    const axiosErr = err as { response?: { status?: number; data?: { error?: string } } }
    const status = axiosErr.response?.status
    const message = axiosErr.response?.data?.error || 'Association failed'
    if (status === 409) return { success: false, message: `Conflict: ${message}` }
    if (status === 404) return { success: false, message: `Not found: ${message}` }
    if (status === 502) return { success: false, message: `Franchise API error: ${message}` }
    return { success: false, message }
  }
}
/**
 * PUT /api/v1/customers/association/{lytsoftCustomerId}
 * Updates the QBO customer association for an existing Lytning debtor.
 */
export async function updateCustomerAssociation(
  lytsoftCustomerId: string,
  qboCustomerId: string
): Promise<{ success: boolean; message: string }> {
  try {
    await axios.put(
      `${BASE_URL}/api/v1/customers/association/${encodeURIComponent(lytsoftCustomerId)}`,
      { qboCustomerId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    )
    return { success: true, message: 'Association updated' }
  } catch (err) {
    const axiosErr = err as { response?: { status?: number; data?: { error?: string } } }
    const status = axiosErr.response?.status
    const message = axiosErr.response?.data?.error || 'Update failed'
    if (status === 409) return { success: false, message: `Conflict: ${message}` }
    if (status === 404) return { success: false, message: `Not found: ${message}` }
    return { success: false, message }
  }
}
// --- Entity Search Types (Phase 12-03) ---
export interface QboItemSearchResult {
  qboItemId: string
  name: string
  type: string
  description: string
  unitPrice: number
}
export interface QboCustomerSearchResult {
  qboCustomerId: string
  name: string
  email: string
  phone: string
}
export interface LytningItemSearchResult {
  lytsoftItemId: string
  name: string
  type: string
  price: number
}
export interface LytningCustomerSearchResult {
  lytsoftCustomerId: string
  name: string
  email: string
  phone: string
}
export interface LytningInvoiceSearchResult {
  lytsoftInvoiceId: string
  name: string
  invoiceDate: string | null
  saleNumber: number | null
  totalAmount: number | null
  customerName: string | null
}
export interface LytningPaymentSearchResult {
  lytsoftPaymentId: string
  name: string
  paymentDate: string | null
  paymentType: string | null
  totalAmount: number | null
  customerName: string | null
}

// --- Entity Search Functions (Phase 12-03) ---
const LYTNING_SEARCH_PAGE_SIZE = 200

type AssociationSearchPage<T> = {
  rows: T[]
  total: number
}

/**
 * Searches the complete paginated Lytning association dataset until enough
 * matches are found. The listing endpoints do not accept a search term, so
 * stopping at page one would make valid link targets unreachable.
 */
async function searchAssociationPages<TSource, TResult>(
  query: string,
  limit: number,
  loadPage: (page: number, pageSize: number) => Promise<AssociationSearchPage<TSource>>,
  matches: (row: TSource, normalizedQuery: string) => boolean,
  mapResult: (row: TSource) => TResult,
): Promise<TResult[]> {
  const normalizedQuery = query.trim().toLowerCase()
  const matchesFound: TResult[] = []
  let page = 1
  let rowsVisited = 0
  let totalRows = Number.POSITIVE_INFINITY

  while (rowsVisited < totalRows && matchesFound.length < limit) {
    const response = await loadPage(page, LYTNING_SEARCH_PAGE_SIZE)
    totalRows = response.total
    rowsVisited += response.rows.length

    for (const row of response.rows) {
      if (matches(row, normalizedQuery)) matchesFound.push(mapResult(row))
      if (matchesFound.length === limit) break
    }

    if (response.rows.length === 0) break
    page += 1
  }

  return matchesFound
}

/** Finds selectable Lytning items across every association page. */
export async function searchLytningItems(query: string, limit = 10): Promise<LytningItemSearchResult[]> {
  return searchAssociationPages(
    query,
    limit,
    async (page, pageSize) => {
      const response = await getItemAssociations({ page, pageSize })
      return { rows: response.items, total: response.total }
    },
    (item, normalizedQuery) => item.itemName.toLowerCase().includes(normalizedQuery),
    item => ({
      lytsoftItemId: item.lytsoftItemId,
      name: item.itemName,
      type: item.itemType ?? '—',
      price: item.unitPrice ?? 0,
    }),
  )
}

/** Finds selectable Lytning customers across every association page. */
export async function searchLytningCustomers(query: string, limit = 10): Promise<LytningCustomerSearchResult[]> {
  return searchAssociationPages(
    query,
    limit,
    async (page, pageSize) => {
      const response = await getCustomerAssociations({ page, pageSize })
      return { rows: response.customers, total: response.total }
    },
    (customer, normalizedQuery) => customer.customerName.toLowerCase().includes(normalizedQuery),
    customer => ({
      lytsoftCustomerId: customer.lytsoftCustomerId,
      name: customer.customerName,
      email: customer.email ?? '—',
      phone: customer.phone ?? '—',
    }),
  )
}

/** Finds selectable Lytning invoices across every association page. */
export async function searchLytningInvoices(query: string, limit = 10): Promise<LytningInvoiceSearchResult[]> {
  return searchAssociationPages(
    query,
    limit,
    async (page, pageSize) => {
      const response = await getInvoiceAssociations({ page, pageSize })
      return { rows: response.invoices, total: response.total }
    },
    (invoice, normalizedQuery) => [
      invoice.saleNumber,
      invoice.lytsoftInvoiceId,
      invoice.customerName,
      invoice.invoiceDate,
    ].some(value => String(value ?? '').toLowerCase().includes(normalizedQuery)),
    invoice => {
      const saleLabel = invoice.saleNumber != null ? `#${invoice.saleNumber}` : invoice.lytsoftInvoiceId
      const customerPart = invoice.customerName ? ` · ${invoice.customerName}` : ''
      const datePart = invoice.invoiceDate ? ` · ${invoice.invoiceDate}` : ''
      return {
        lytsoftInvoiceId: invoice.lytsoftInvoiceId,
        name: `${saleLabel}${customerPart}${datePart}`,
        invoiceDate: invoice.invoiceDate,
        saleNumber: invoice.saleNumber,
        totalAmount: invoice.totalAmount,
        customerName: invoice.customerName,
      }
    },
  )
}

/** Finds selectable Lytning payments across every association page. */
export async function searchLytningPayments(query: string, limit = 10): Promise<LytningPaymentSearchResult[]> {
  return searchAssociationPages(
    query,
    limit,
    async (page, pageSize) => {
      const response = await getPaymentAssociations({ page, pageSize })
      return { rows: response.payments, total: response.total }
    },
    (payment, normalizedQuery) => {
      const haystack = [
        payment.lytsoftPaymentId,
        payment.customerName,
        payment.paymentType,
        payment.paymentDate,
        payment.qboPaymentId,
        ...(payment.linkedInvoiceNumbers ?? []),
      ]
        .filter(Boolean)
        .map(value => String(value).toLowerCase())
        .join(' ')
      return haystack.includes(normalizedQuery)
    },
    payment => {
      const paymentTypeOrId = payment.paymentType ?? payment.lytsoftPaymentId
      const customerPart = payment.customerName ? ` · ${payment.customerName}` : ''
      const datePart = payment.paymentDate ? ` · ${payment.paymentDate}` : ''
      return {
        lytsoftPaymentId: payment.lytsoftPaymentId,
        name: `${paymentTypeOrId}${customerPart}${datePart}`,
        paymentDate: payment.paymentDate,
        paymentType: payment.paymentType,
        totalAmount: payment.totalAmount,
        customerName: payment.customerName,
      }
    },
  )
}

// QBO item search via backend endpoint
export async function searchQboItems(query: string, limit = 10): Promise<QboItemSearchResult[]> {
  const res = await getWithRetry<QboItemSearchResult[]>(
    `/api/v1/items/qbo-search?q=${encodeURIComponent(query)}&limit=${limit}`,
    { headers: authHeaders() }
  )
  return res
}

// QBO customer search via backend endpoint
export async function searchQboCustomers(query: string, limit = 10): Promise<QboCustomerSearchResult[]> {
  const res = await getWithRetry<QboCustomerSearchResult[]>(
    `/api/v1/customers/qbo-search?q=${encodeURIComponent(query)}&limit=${limit}`,
    { headers: authHeaders() }
  )
  return res
}
// Get QBO item details by ID (for dialog display)
export async function getQboItemDetails(qboItemId: string): Promise<QboItemSearchResult | null> {
  console.warn('QBO item details requires backend endpoint GET /api/v1/items/qbo/{qboItemId}')
  return null
}
// Get QBO customer details by ID
export async function getQboCustomerDetails(qboCustomerId: string): Promise<QboCustomerSearchResult | null> {
  console.warn('QBO customer details requires backend endpoint GET /api/v1/customers/qbo/{qboCustomerId}')
  return null
}
// ─── Paged QBO Search + Link Actions (Phase 24) ───────────────────────────────

export type ItemAssociationLinkResponse = LinkResponse<ItemAssociationRow>
export type CustomerAssociationLinkResponse = LinkResponse<CustomerAssociationRow>

// QBO item paged search (Phase 24, extended by Phase 28 for linked filter)
export async function searchQboItemsPaged(
  query: string,
  page: number,
  size: number,
  options?: { linked?: boolean }
): Promise<{ items: QboItemSearchResult[]; totalCount: number; page: number; size: number }> {
  const linkedParam = options?.linked === undefined ? '' : `&linked=${options.linked}`
  const res = await getWithRetry<{ items?: QboItemSearchResult[]; totalCount?: number; page?: number; size?: number }>(
    `/api/v1/items/search-qbo?query=${encodeURIComponent(query)}&page=${page}&size=${size}${linkedParam}`,
    { headers: authHeaders() }
  )
  const list = Array.isArray(res?.items) ? res.items : []
  return {
    items: list,
    totalCount: res?.totalCount ?? list.length,
    page: res?.page ?? page,
    size: res?.size ?? size,
  }
}

// GET /api/v1/items/link?qboId=&lytId=
export async function getItemLink(
  qboItemId?: string,
  lytsoftItemId?: string
): Promise<LinkResponse<ItemAssociationRow>> {
  const params = new URLSearchParams()
  if (qboItemId) params.append('qboId', qboItemId)
  if (lytsoftItemId) params.append('lytId', lytsoftItemId)
  return getWithRetry<LinkResponse<ItemAssociationRow>>(
    `/api/v1/items/link?${params.toString()}`,
    { headers: authHeaders() }
  )
}

// POST /api/v1/items/link { qboItemId, lytsoftItemId }
export async function postItemLink(
  qboItemId: string,
  lytsoftItemId: string
): Promise<LinkResponse<ItemAssociationRow>> {
  try {
    const res = await axios.post<LinkResponse<ItemAssociationRow>>(
      `${BASE_URL}/api/v1/items/link`,
      { qboItemId, lytsoftItemId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    )
    return res.data
  } catch (err) {
    return classifyLinkError<ItemAssociationRow>(err)
  }
}


// QBO customer paged search (Phase 24, extended by Phase 28 for linked filter)
export async function searchQboCustomersPaged(
  query: string,
  page: number,
  size: number,
  options?: { linked?: boolean }
): Promise<{ items: QboCustomerSearchResult[]; totalCount: number; page: number; size: number }> {
  const linkedParam = options?.linked === undefined ? '' : `&linked=${options.linked}`
  const res = await getWithRetry<{ customers?: QboCustomerSearchResult[]; totalCount?: number; page?: number; size?: number }>(
    `/api/v1/customers/search-qbo?query=${encodeURIComponent(query)}&page=${page}&size=${size}${linkedParam}`,
    { headers: authHeaders() }
  )
  const list = Array.isArray(res?.customers) ? res.customers : []
  return {
    items: list,
    totalCount: res?.totalCount ?? list.length,
    page: res?.page ?? page,
    size: res?.size ?? size,
  }
}

// GET /api/v1/customers/link?qboId=&lytId=
export async function getCustomerLink(
  qboCustomerId?: string,
  lytsoftCustomerId?: string
): Promise<LinkResponse<CustomerAssociationRow>> {
  const params = new URLSearchParams()
  if (qboCustomerId) params.append('qboId', qboCustomerId)
  if (lytsoftCustomerId) params.append('lytId', lytsoftCustomerId)
  return getWithRetry<LinkResponse<CustomerAssociationRow>>(
    `/api/v1/customers/link?${params.toString()}`,
    { headers: authHeaders() }
  )
}

// POST /api/v1/customers/link { qboCustomerId, lytsoftCustomerId }
export async function postCustomerLink(
  qboCustomerId: string,
  lytsoftCustomerId: string
): Promise<LinkResponse<CustomerAssociationRow>> {
  try {
    const res = await axios.post<LinkResponse<CustomerAssociationRow>>(
      `${BASE_URL}/api/v1/customers/link`,
      { qboCustomerId, lytsoftCustomerId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    )
    return res.data
  } catch (err) {
    return classifyLinkError<CustomerAssociationRow>(err)
  }
}

export type InvoiceAssociationLinkResponse = LinkResponse<InvoiceAssociationRow>
export type PaymentAssociationLinkResponse = LinkResponse<PaymentAssociationRow>
export interface QboInvoiceSearchResult {
  qboInvoiceId: string
  docNumber: string
  customerRef: { name: string; value: string }
  totalAmt: number
  txnDate: string
  balance: number
}
export interface QboPaymentSearchResult {
  qboPaymentId: string
  docNumber: string
  entity: string
  vendorRef: { name: string; value: string }
  totalAmt: number
  txnDate: string
}
// ─── Paged QBO Search + Link Actions (Phase 25) ───────────────────────────────

// QBO invoice paged search (Phase 25, extended by Phase 28 for linked filter)
export async function searchQboInvoicesPaged(
  query: string,
  page: number,
  size: number,
  options?: { linked?: boolean }
): Promise<{ items: QboInvoiceSearchResult[]; totalCount: number; page: number; size: number }> {
  const linkedParam = options?.linked === undefined ? '' : `&linked=${options.linked}`
  const res = await getWithRetry<{ invoices?: QboInvoiceSearchResult[]; totalCount?: number; page?: number; size?: number }>(
    `/api/v1/invoices/search-qbo?query=${encodeURIComponent(query)}&page=${page}&size=${size}${linkedParam}`,
    { headers: authHeaders() }
  )
  const list = Array.isArray(res?.invoices) ? res.invoices : []
  return {
    items: list,
    totalCount: res?.totalCount ?? list.length,
    page: res?.page ?? page,
    size: res?.size ?? size,
  }
}

// GET /api/v1/invoices/link?qboId=&lytId=
export async function getInvoiceLink(
  qboInvoiceId?: string,
  lytsoftInvoiceId?: string
): Promise<LinkResponse<InvoiceAssociationRow>> {
  const params = new URLSearchParams()
  if (qboInvoiceId) params.append('qboId', qboInvoiceId)
  if (lytsoftInvoiceId) params.append('lytId', lytsoftInvoiceId)
  return getWithRetry<LinkResponse<InvoiceAssociationRow>>(
    `/api/v1/invoices/link?${params.toString()}`,
    { headers: authHeaders() }
  )
}

// POST /api/v1/invoices/link { qboInvoiceId, lytsoftInvoiceId }
export async function postInvoiceLink(
  qboInvoiceId: string,
  lytsoftInvoiceId: string
): Promise<LinkResponse<InvoiceAssociationRow>> {
  try {
    const res = await axios.post<LinkResponse<InvoiceAssociationRow>>(
      `${BASE_URL}/api/v1/invoices/link`,
      { qboInvoiceId, lytsoftInvoiceId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    )
    return res.data
  } catch (err) {
    return classifyLinkError<InvoiceAssociationRow>(err)
  }
}


// QBO payment paged search (Phase 25, extended by Phase 28 for linked filter)
export async function searchQboPaymentsPaged(
  query: string,
  page: number,
  size: number,
  options?: { linked?: boolean }
): Promise<{ items: QboPaymentSearchResult[]; totalCount: number; page: number; size: number }> {
  const linkedParam = options?.linked === undefined ? '' : `&linked=${options.linked}`
  const res = await getWithRetry<{ payments?: QboPaymentSearchResult[]; totalCount?: number; page?: number; size?: number }>(
    `/api/v1/payments/search-qbo?query=${encodeURIComponent(query)}&page=${page}&size=${size}${linkedParam}`,
    { headers: authHeaders() }
  )
  const list = Array.isArray(res?.payments) ? res.payments : []
  return {
    items: list,
    totalCount: res?.totalCount ?? list.length,
    page: res?.page ?? page,
    size: res?.size ?? size,
  }
}

// GET /api/v1/payments/link?qboId=&lytId=
export async function getPaymentLink(
  qboPaymentId?: string,
  lytsoftPaymentId?: string
): Promise<LinkResponse<PaymentAssociationRow>> {
  const params = new URLSearchParams()
  if (qboPaymentId) params.append('qboId', qboPaymentId)
  if (lytsoftPaymentId) params.append('lytId', lytsoftPaymentId)
  return getWithRetry<LinkResponse<PaymentAssociationRow>>(
    `/api/v1/payments/link?${params.toString()}`,
    { headers: authHeaders() }
  )
}
// POST /api/v1/payments/link { qboPaymentId, lytsoftPaymentId }
export async function postPaymentLink(
  qboPaymentId: string,
  lytsoftPaymentId: string
): Promise<LinkResponse<PaymentAssociationRow>> {
  try {
    const res = await axios.post<LinkResponse<PaymentAssociationRow>>(
      `${BASE_URL}/api/v1/payments/link`,
      { qboPaymentId, lytsoftPaymentId },
      { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
    )
    return res.data
  } catch (err) {
    return classifyLinkError<PaymentAssociationRow>(err)
  }
}
// --- Dialog helper: check if entity is already associated ---
export function isAlreadyAssociated(entity: ItemAssociationRow | CustomerAssociationRow): boolean {
  if ('qboItemId' in entity && entity.qboItemId) return true
  if ('qboCustomerId' in entity && entity.qboCustomerId) return true
  return false
}
// --- Dialog helper: get association info ---
export function getAssociationInfo(entity: ItemAssociationRow | CustomerAssociationRow): { provider: string; providerId: string; syncedAt: string | null } | null {
  if ('qboItemId' in entity && entity.qboItemId) {
    return {
      provider: 'QBO',
      providerId: entity.qboItemId,
      syncedAt: entity.syncedAt
    }
  }
  if ('qboCustomerId' in entity && entity.qboCustomerId) {
    return {
      provider: 'QBO',
      providerId: entity.qboCustomerId,
      syncedAt: entity.syncedAt
    }
  }
  return null
}
